import styled, { css } from 'styled-components';
import { stacked } from 'app/shared/styles/_spacing';
import { font } from 'app/shared/styles/_fontsize';
import { colors } from 'app/shared/styles/_colors';
import Linker from 'app/shared/modules/Linker';
import Container from 'app/shared/core/Container';
import Title from 'app/shared/modules/Title';
import { viewports } from 'app/shared/styles/_breakpoints';
import type { ComponentType } from 'react';

interface AccordionProps {
  isOpen?: boolean;
}

export const StyledContainer: ComponentType<React.ComponentProps<typeof Container>> = styled(Container)`
  padding: 40px 16px;

  @media ${viewports['md-and-up']} {
    padding: 40px 24px;
  }
`;

export const StyledTitle = styled(Title)`
  ${font.md};
  font-weight: normal;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  margin: 0;
`;

export const PlaceholderTitle = styled.div`
  height: 48px;
  visibility: hidden;
`;

export const ColumnContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media ${viewports['md-and-up']} {
    flex-direction: row;
    gap: 24px;
    align-items: flex-start;
    width: 100%;
    flex-wrap: wrap;
  }

  @media ${viewports['lg-and-up']} {
    gap: 20px;
  }
`;

export const Column = styled.div`
  width: 100%;
`;

export const AccordionHeader = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  cursor: pointer;
  padding: 16px 0;
  border-bottom: 1px solid ${colors['$hpx-grey-400']};

  @media ${viewports['md-and-up']} {
    display: none;
  }
`;

export const FirstAccordionHeader = styled(AccordionHeader)`
  border-top: 1px solid ${colors['$hpx-grey-400']};
`;

export const AccordionContent = styled.div<AccordionProps>`
  position: relative;
  display: ${(props) => (props.isOpen ? 'block' : 'none')};
  padding-top: 16px;

  @media (min-width: 768px) {
    position: static;
    display: block;
    padding-top: 0;
  }
`;

export const AccordionTitle = styled.h2`
  ${font.md};
  font-weight: normal;
  margin: 0;
  color: ${colors['$hpx-blue-600']};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  margin-left: 8px;
  display: flex;
  align-items: center;

  @media ${viewports['md-and-up']} {
    ${font.md};
  }
`;

export const AccordionIcon = styled.img<AccordionProps>`
  width: 12px;
  height: 12px;
  transition: transform 0.3s ease;
  transform: ${(props) => (props.isOpen ? 'rotate(180deg)' : 'rotate(0)')};
`;

export const CityList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0;
`;

export const CityItem = styled.li<{ isVisible?: boolean }>`
  padding-right: 8px;
  &:not(:last-of-type) {
    ${stacked._1x};
  }

  ${(props) =>
    !props.isVisible &&
    css`
      position: absolute;
      left: -9999px;
      top: -9999px;
    `}
`;

export const StyledLink = styled(Linker)`
  display: block;
  &:focus {
    outline-offset: 4px;
  }
  ${font.sm};
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  color: ${colors['$hpx-teal-500']};
  text-decoration: none;

  &:hover {
    color: ${colors['$hpx-teal-600']};
  }
`;

export const ShowMoreButton = styled.button`
  border: none;
  background: none;
  ${font.tiny};
  cursor: pointer;
  color: ${colors['$hpx-grey-600']};
  display: flex;
  align-items: center;
  margin-top: 16px;

  &:hover {
    color: ${colors['$hpx-blue-600']};
  }
`;

export const ShowMoreCarat = styled.img`
  padding-left: 4px;
`;

export const DesktopTitle = styled.div`
  display: none;

  @media (min-width: 768px) {
    display: block;
    height: 48px;
    display: flex;
    align-items: center;
  }
`;

export const MobileStatesContainer = styled.div`
  display: block;

  @media (min-width: 768px) {
    display: none;
  }
`;

export const RentalTypesContainer = styled.div`
  display: grid;
  gap: 16px;
  width: 100%;

  grid-template-columns: 1fr;

  /* Tablet: 2 columns */
  @media ${viewports['md-and-up']} {
    grid-template-columns: repeat(2, 1fr);
    gap: 24px;
  }

  /* Desktop: 4 columns at 1020px */
  @media (min-width: 1020px) {
    grid-template-columns: repeat(4, 1fr);
    gap: 20px;
  }

  @media ${viewports['xl-and-up']} {
    grid-template-columns: repeat(4, minmax(200px, 1fr));
  }
`;

export const DesktopStatesContainer = styled.div`
  display: none;

  @media ${viewports['md-and-up']} {
    display: grid;
    grid-auto-flow: column;
    grid-template-rows: 1fr;
    gap: 24px;
    width: 100%;
  }

  @media ${viewports['lg-and-up']} {
    grid-auto-flow: column;
    grid-template-rows: 1fr;
    gap: 20px;
  }
`;

export const StateColumn = styled.div`
  width: 100%;
`;

export const SectionItem = styled.li`
  width: 100%;
  padding-bottom: 16px;
  border-bottom: 1px solid ${colors['$hpx-grey-400']};

  &:last-child {
    border-bottom: none;
  }

  @media ${viewports['md-and-up']} {
    width: 25%;
    border-bottom: none;
    padding-bottom: 0;
  }
`;
